import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Globe, Leaf, Award, ChefHat, MapPin, Utensils } from 'lucide-react';

export default function AboutPage() {
  const values = [
    {
      icon: <Heart className="text-terracotta" size={28} />,
      title: 'Passion',
      description: 'Chaque plat est préparé avec amour et dévouement, comme à la maison.',
    },
    {
      icon: <Globe className="text-terracotta" size={28} />,
      title: 'Authenticité',
      description: 'Des recettes du monde entier, fidèles aux traditions culinaires.',
    },
    {
      icon: <Leaf className="text-terracotta" size={28} />,
      title: 'Qualité',
      description: 'Ingrédients frais, locaux et de saison sélectionnés avec soin.',
    },
    {
      icon: <Award className="text-terracotta" size={28} />,
      title: 'Excellence',
      description: 'Un service irréprochable de la commande à la livraison.',
    },
  ];

  return (
    <main className="min-h-screen bg-transparent pt-24 pb-16">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
                Notre Histoire
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-navy leading-tight">
                Des racines profondes,{' '}
                <span className="text-terracotta">des saveurs du monde</span>
              </h1>
              <p className="text-navy/70 text-lg leading-relaxed">
                Fondée à Genève, Racines et Épices du Monde est née d'une passion pour 
                la cuisine authentique et le désir de partager les saveurs qui nous ont 
                fait voyager à travers les continents.
              </p>
              <p className="text-navy/70 leading-relaxed">
                Notre mission est simple : vous offrir des plats faits maison, préparés 
                avec des ingrédients de qualité, qui évoquent la chaleur d'un repas en 
                famille, où que vous soyez dans le monde.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden">
                <img
                  src="/generated/histoire_table_bois.jpg"
                  alt="Grande table en vieux bois garnie d'aliments frais"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing bg-white/50">
        <div className="container-padding max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center space-y-4 mb-16"
          >
            <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
              Nos Valeurs
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              Ce qui nous anime
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-card text-center"
              >
                <div className="w-14 h-14 mx-auto bg-terracotta/10 rounded-xl flex items-center justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className="font-heading text-xl font-semibold text-navy mb-2">
                  {value.title}
                </h3>
                <p className="text-navy/60 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Chef Story */}
      <section className="section-spacing">
        <div className="container-padding max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center space-y-6"
          >
            <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
              Notre Chef
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#3E2723]">
              Miloud
            </h2>
            <p className="text-[#6D4C41] leading-relaxed max-w-2xl mx-auto">
              Passionné par la gastronomie depuis ses débuts, notre chef a forgé son 
              savoir-faire auprès de chefs étoilés, acquérant une maîtrise rare qui allie 
              rigueur technique et générosité culinaire.
            </p>
            <p className="text-[#6D4C41] leading-relaxed max-w-2xl mx-auto">
              Fort de <strong className="text-[#3E2723]">30 années d'expérience</strong> exercées 
              sur la scène internationale en France, au Luxembourg, en Angleterre et en Suisse, 
              il s'est imposé comme un traiteur de référence, au service aussi bien des 
              particuliers que des entreprises.
            </p>
            
            <blockquote className="border-l-4 border-terracotta pl-6 py-2 italic text-[#3E2723] text-left max-w-xl mx-auto bg-[#FAF8F4] rounded-r-xl">
              « Parce que chaque repas mérite d'être une expérience, je mets mon excellence 
              au service de votre table. »
            </blockquote>

            <div className="flex justify-center gap-8 pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-terracotta/10 rounded-xl flex items-center justify-center">
                  <ChefHat className="text-terracotta" size={24} />
                </div>
                <div className="text-left">
                  <p className="font-bold text-[#3E2723] text-xl">30 ans</p>
                  <p className="text-sm text-[#6D4C41]">d'expérience</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-terracotta/10 rounded-xl flex items-center justify-center">
                  <MapPin className="text-terracotta" size={24} />
                </div>
                <div className="text-left">
                  <p className="font-bold text-[#3E2723] text-xl">4 pays</p>
                  <p className="text-sm text-[#6D4C41]">d'expérience</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chef Engagements */}
      <section className="section-spacing bg-[#FAF8F4]">
        <div className="container-padding max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center space-y-4 mb-12"
          >
            <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
              Nos Engagements
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#3E2723]">
              La philosophie du Chef
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Leaf className="text-sage" size={24} />,
                title: "Produits locaux",
                description: "Circuits courts pour une cuisine fraîche, responsable et de saison."
              },
              {
                icon: <Heart className="text-terracotta" size={24} />,
                title: "Zéro conservateur",
                description: "Des plats 100% faits maison, sans additifs ni compromis."
              },
              {
                icon: <Utensils className="text-ocre" size={24} />,
                title: "Recettes familiales",
                description: "L'authenticité et la chaleur du fait maison à votre table."
              },
              {
                icon: <Award className="text-sage" size={24} />,
                title: "Régimes spéciaux",
                description: "Végétarien, sans gluten, intolérance au lactose et plus encore."
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-card text-center"
              >
                <div className="w-12 h-12 mx-auto bg-[#D4C4AA]/30 rounded-xl flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#3E2723] mb-2">
                  {item.title}
                </h3>
                <p className="text-[#6D4C41] text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-spacing bg-[#3E2723] text-white">
        <div className="container-padding max-w-4xl mx-auto text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              Basés à Genève
            </h2>
            <p className="text-white/70 max-w-xl mx-auto">
              Nous livrons dans tout le canton de Genève du lundi au samedi. 
              Choisissez votre créneau de livraison et savourez !
            </p>
          </motion.div>

          <div className="bg-white/10 rounded-2xl p-6 inline-block">
            <p className="text-sm text-white/60 mb-1">Notre adresse</p>
            <p className="text-lg font-medium">
              Rue Pré-Bouvier 29, 1242 Satigny
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
